import { computed, type ComputedRef, type Ref } from 'vue'
import {
  assessSourceAmount,
  calculateSpendableBalanceRaw,
  getSmartDefaultAmount,
  formatTokenBalance,
} from './token-policy'
import type { WalletSourceToken } from './contracts'
import { fromSmallestUnits, parseDecimalUnits } from '../domain/amounts'

interface Deps {
  sourceAmount: Readonly<Ref<string>>
  selectedToken: ComputedRef<WalletSourceToken | undefined>
  selectedChainId: Readonly<Ref<number>>
  isLoadingTokens: Readonly<Ref<boolean>>
  selectSourceToken: (tokenAddress: string, sourceAmount: string) => void
  setSourceAmount: (sourceAmount: string) => void
  invalidateRouteContext: (preserveQuote?: boolean) => void
  routeLocked?: Readonly<Ref<boolean>>
}

export function useAmountInput({
  sourceAmount,
  selectedToken,
  selectedChainId,
  isLoadingTokens,
  selectSourceToken,
  setSourceAmount,
  invalidateRouteContext,
  routeLocked,
}: Deps) {
  const sourceAmountUsd = computed(() => {
    if (!selectedToken.value?.priceUsd || !sourceAmount.value) return null
    const amount = parseFloat(sourceAmount.value)
    if (isNaN(amount) || amount <= 0) return null
    const usd = amount * selectedToken.value.priceUsd
    return usd >= 0.01 ? `$${usd.toFixed(2)}` : `<$0.01`
  })

  const balanceUsd = computed(() => {
    if (!selectedToken.value?.priceUsd || !selectedToken.value.balanceFormatted) return null
    const balance = parseFloat(selectedToken.value.balanceFormatted)
    if (isNaN(balance) || balance <= 0) return null
    const usd = balance * selectedToken.value.priceUsd
    return usd >= 0.01 ? `($${usd.toFixed(2)})` : `(<$0.01)`
  })

  const spendableBalanceRaw = computed(() => {
    if (!selectedToken.value) return 0n
    return calculateSpendableBalanceRaw(selectedToken.value, selectedChainId.value)
  })

  const spendableBalanceLabel = computed(() => {
    if (!selectedToken.value?.hasBalance || selectedToken.value.balanceStatus === 'unavailable') {
      return null
    }

    const spendable = spendableBalanceRaw.value
    if (spendable <= 0n) return null

    return `${formatTokenBalance(fromSmallestUnits(spendable.toString(), selectedToken.value.decimals))} ${selectedToken.value.symbol}`
  })

  const quickAmountOptions = computed(() => {
    const token = selectedToken.value
    if (!token?.hasBalance || token.balanceStatus === 'unavailable') return []

    const spendable = spendableBalanceRaw.value
    if (spendable <= 0n) return []

    const suggestions = [
      { label: '50%', value: fromSmallestUnits((spendable / 2n).toString(), token.decimals) },
      { label: 'Max', value: fromSmallestUnits(spendable.toString(), token.decimals) },
    ]

    return suggestions.filter(
      (option, index, array) =>
        option.value !== '0' &&
        array.findIndex((candidate) => candidate.value === option.value) === index
    )
  })

  const amountValidationError = computed(() => {
    const token = selectedToken.value
    if (!token || isLoadingTokens.value) return null
    return assessSourceAmount(sourceAmount.value, token, selectedChainId.value).validationError
  })

  const selectToken = (token: WalletSourceToken) => {
    if (routeLocked?.value) return
    selectSourceToken(token.address, getSmartDefaultAmount(token, selectedChainId.value))
    invalidateRouteContext()
  }

  const applyQuickAmount = (value: string) => {
    if (routeLocked?.value) return
    const token = selectedToken.value
    if (!token) return
    const amount = parseDecimalUnits(value, token.decimals)
    if (amount === null || amount <= 0n || amount > spendableBalanceRaw.value) return
    setSourceAmount(value)
  }

  const setMaxAmount = () => {
    if (routeLocked?.value) return
    const token = selectedToken.value
    if (!token?.hasBalance) return

    const spendable = calculateSpendableBalanceRaw(token, selectedChainId.value)
    if (spendable <= 0n) return
    setSourceAmount(fromSmallestUnits(spendable.toString(), token.decimals))
  }

  const handleAmountInput = (event: Event) => {
    if (routeLocked?.value) return
    const input = event.target as HTMLInputElement
    let value = input.value

    // Normalize comma to dot so RU/EU keyboards can enter decimals.
    value = value.replace(',', '.')
    // Remove invalid characters (allow only numbers and single dot)
    value = value.replace(/[^0-9.]/g, '')

    // Ensure only one decimal point
    const parts = value.split('.')
    if (parts.length > 2) {
      value = (parts[0] ?? '') + '.' + parts.slice(1).join('')
    }

    // Prevent lone dot as stable state
    if (value === '.') {
      value = ''
    }

    // Trim leading zeros (but keep one zero if it's "0.something")
    if (parts.length === 1 && value.length > 1 && value.startsWith('0')) {
      value = value.replace(/^0+/, '') || '0'
    } else if (parts.length === 2 && parts[0] && parts[0].length > 1 && parts[0].startsWith('0')) {
      value = '0.' + (parts[1] ?? '')
    }

    // Update input and reactive value if changed
    if (value !== input.value) {
      input.value = value
    }
    if (value !== sourceAmount.value) {
      setSourceAmount(value)
    }
  }

  return {
    sourceAmountUsd,
    balanceUsd,
    spendableBalanceLabel,
    quickAmountOptions,
    amountValidationError,
    selectToken,
    applyQuickAmount,
    setMaxAmount,
    handleAmountInput,
  }
}
